import * as THREE from 'three'

/**
 * GLB 导入模型的基准槽位类型。
 */
export type GlbDatumSlotKind = 'front-plane' | 'top-plane' | 'right-plane' | 'origin'

/**
 * GLB 基准槽位目标描述符。
 */
export interface GlbDatumSlotTargetDescriptor {
  /** 标签页 ID。 */
  tabId: string
  /** 宿主对象 ID。 */
  hostObjectId: string
  /** 槽位类型。 */
  slotKind: GlbDatumSlotKind
}

/**
 * GLB 基准平面 frame。
 * 说明：
 * 1. 这是 info-only 解析结果，不是可编辑的正式基准面对象；
 * 2. 仅承载渲染或后续选择层所需的世界坐标信息。
 */
export interface GlbDatumPlaneFrame {
  /** frame 类型。 */
  kind: 'plane'
  /** 槽位类型。 */
  slotKind: Exclude<GlbDatumSlotKind, 'origin'>
  /** 平面原点（世界坐标）。矩形轮廓也以此为中心绘制。 */
  origin: THREE.Vector3
  /** 平面 X 轴（单位向量）。 */
  xDir: THREE.Vector3
  /** 平面 Y 轴（单位向量）。 */
  yDir: THREE.Vector3
  /** 平面法线（单位向量）。 */
  normal: THREE.Vector3
  /** 平面显示宽度。 */
  width: number
  /** 平面显示高度。 */
  height: number
}

/**
 * GLB 基准原点 frame。
 * 说明：
 * 1. 仅保留 info-only 需要的世界原点与显示尺寸提示；
 * 2. 后续 renderer 可再决定如何把 sizePx 映射到实际像素或世界尺寸。
 */
export interface GlbDatumOriginFrame {
  /** frame 类型。 */
  kind: 'origin'
  /** 槽位类型。 */
  slotKind: 'origin'
  /** 原点（世界坐标）。 */
  origin: THREE.Vector3
  /** 显示尺寸提示。 */
  sizePx: number
}

/**
 * GLB 基准槽位 frame 联合类型。
 */
export type GlbDatumSlotFrame = GlbDatumPlaneFrame | GlbDatumOriginFrame

/**
 * 判断两个基准槽位描述符是否表示同一个目标。
 *
 * @param left 左侧描述符。
 * @param right 右侧描述符。
 * @returns 是否完全相同。
 */
export function isSameGlbDatumSlotDescriptor(
  left: GlbDatumSlotTargetDescriptor | null | undefined,
  right: GlbDatumSlotTargetDescriptor | null | undefined,
): boolean {
  if (left === right) {
    return true
  }
  if (!left || !right) {
    return false
  }
  return left.tabId === right.tabId
    && left.hostObjectId === right.hostObjectId
    && left.slotKind === right.slotKind
}
